// src/contexts/AuthContext.js

import React, { createContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // e.g., 'admin', 'user'
  const [loading, setLoading] = useState(true);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [hasValidSubscription, setHasValidSubscription] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Fetch user role and subscription info from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserRole(userData.role || 'user');
          setSubscriptionTier(userData.subscriptionTier || 'none');
          
          // Check if user has a valid subscription
          // Valid if:
          // 1. They have a free tier, OR
          // 2. It's a paid subscription tier other than 'none' AND either:
          //    a. It's a lifetime subscription, OR
          //    b. It's a time-limited subscription (like sixMonth) that hasn't expired yet, OR
          //    c. They are marked as a beta user
          const isFree = userData.subscriptionTier === 'free';
          const validTier = userData.subscriptionTier && userData.subscriptionTier !== 'none';
          const isLifetime = userData.subscriptionTier === 'lifetime';
          const isBetaUser = userData.isBetaUser === true;
          // Check if the subscription is not expired
          let notExpired = true; // Default to true if no expiration date
          if (userData.expirationDate) {
            try {
              const expirationDate = userData.expirationDate.toDate ? 
                userData.expirationDate.toDate() : new Date(userData.expirationDate);
              notExpired = expirationDate > new Date();
            } catch (error) {
              console.error('Error parsing expiration date:', error);
              notExpired = false; // Assume expired if can't parse date
            }
          }
          
          const hasValid = isFree || (validTier && (isLifetime || isBetaUser || notExpired));
          
          // Debug logging for subscription status
          console.log('Subscription status check:', {
            email: user.email,
            tier: userData.subscriptionTier,
            isFree,
            validTier,
            isLifetime,
            isBetaUser,
            notExpired,
            hasValidSubscription: hasValid
          });
          
          setHasValidSubscription(hasValid);
        } else {
          // Default to no role until proper verification
          setUserRole(null);
          setSubscriptionTier('none');
          setHasValidSubscription(false);
          
          // Create user document with limited permissions
          try {
            const userRef = doc(db, 'users', user.uid);
            // Need to import setDoc
            const { setDoc } = await import('firebase/firestore');
            await setDoc(userRef, {
              email: user.email,
              createdAt: new Date(),
              role: 'user',
              subscriptionTier: 'free',
              videoCount: 0
            }, { merge: true });
            setUserRole('user'); // Only set role after document is created
          } catch (error) {
            console.error('Error creating user document:', error);
          }
        }
      } else {
        setUserRole(null);
        setSubscriptionTier(null);
        setHasValidSubscription(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      userRole, 
      loading, 
      subscriptionTier,
      hasValidSubscription
    }}>
      {children}
    </AuthContext.Provider>
  );
}
